import { Box, CircularProgress, Tooltip, Typography } from "@mui/material";

const STATUS_TONE_STYLES = {
  success: {
    backgroundColor: "#dcfce7",
    color: "#166534",
    border: "1px solid #bbf7d0",
  },
  warning: {
    backgroundColor: "#ffedd5",
    color: "#9a3412",
    border: "1px solid #fed7aa",
  },
  danger: {
    backgroundColor: "#fee2e2",
    color: "#b91c1c",
    border: "1px solid #fecaca",
  },
  muted: {
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "1px solid #d1d5db",
  },
  default: {
    backgroundColor: "#f3f4f6",
    color: "#374151",
    border: "1px solid #d1d5db",
  },
};

function normalizeStatusValue(value) {
  if (value && typeof value === "object") {
    return {
      label: value.label ?? "",
      tone: value.tone ?? value.variant ?? "",
      tooltip: value.tooltip ?? "",
      interactive: Boolean(value.interactive),
    };
  }

  return {
    label: value ?? "",
    tone: "",
    tooltip: "",
    interactive: false,
  };
}

function resolveStatusStyles(value) {
  const statusData = normalizeStatusValue(value);
  const normalizedLabel = statusData.label.toLowerCase();
  const normalizedTone = statusData.tone.toLowerCase();

  const pickTone = (tone) =>
    STATUS_TONE_STYLES[tone] || STATUS_TONE_STYLES.default;

  if (normalizedTone) {
    return { ...statusData, styles: pickTone(normalizedTone) };
  }

  switch (normalizedLabel) {
    case "connected":
    case "synced":
    case "healthy":
    case "recent":
      return { ...statusData, styles: pickTone("success") };
    case "disconnected":
    case "failed":
    case "outdated":
      return { ...statusData, styles: pickTone("danger") };
    case "partial":
    case "sync needed":
    case "sync issues":
    case "validation pending":
      return { ...statusData, styles: pickTone("warning") };
    case "not required":
    case "not synced":
      return { ...statusData, styles: pickTone("muted") };
    default:
      return { ...statusData, styles: pickTone("default") };
  }
}

export default function renderQuickbooksStatusCell({
  value,
  column,
  row,
  validationLoadingCustomerId,
  onSyncStatusClick,
}) {
  const customer = row?.originalCustomer || row;
  const statusData = resolveStatusStyles(value);
  const isSyncStatusColumn = column.field === "syncStatus";
  const statusCustomerId = customer?.id || row?.customerId || customer?.customerId;

  const isStatusLoading =
    isSyncStatusColumn &&
    statusData.interactive &&
    statusCustomerId &&
    statusCustomerId === validationLoadingCustomerId;

  const canClick =
    isSyncStatusColumn &&
    statusData.interactive &&
    typeof onSyncStatusClick === "function";

  const chip = (
    <Typography
      component="span"
      onClick={
        canClick
          ? (event) => {
              event.stopPropagation();
              onSyncStatusClick(customer, statusData);
            }
          : undefined
      }
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: 0.75,
        borderRadius: "16px",
        fontWeight: 500,
        fontSize: "0.75rem",
        px: 1.5,
        py: 0.5,
        cursor: canClick ? "pointer" : "default",
        ...statusData.styles,
      }}
    >
      {isStatusLoading ? (
        <CircularProgress size={12} sx={{ color: "#4B5563" }} />
      ) : null}
      {statusData.label || "—"}
    </Typography>
  );

  if (!statusData.tooltip) {
    return chip;
  }

  return (
    <Tooltip title={statusData.tooltip} arrow>
      <Box component="span">{chip}</Box>
    </Tooltip>
  );
}